import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { BLEEP_SERVER_NAMES, PURGATORY_REVIEW_STATUSES } from '../../utils/constants';
import {
    stageOnboardingPurgatory,
    approvePurgatoryItem,
    rejectPurgatoryItem,
} from '../../services/onboardingService';
import PurgatoryToolbar from './PurgatoryToolbar';
import PurgatoryCard from './PurgatoryCard';
import HourlySyncStatusBar from './HourlySyncStatusBar';
import {
    FaSpinner,
    FaCheckCircle,
    FaExclamationCircle,
    FaInbox,
    FaExclamationTriangle,
    FaTimes,
} from 'react-icons/fa';

const PurgatoryReviewTab = ({ serverID }) => {
    const [, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [staging, setStaging] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('pending');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [busyItemIds, setBusyItemIds] = useState(new Set());
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }
    const [showStageConfirmModal, setShowStageConfirmModal] = useState(false);

    // 1. Debounce search query changes by 300ms
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    // 2. Real-time Firestore subscription on collection `servers/{serverID}/onboardingPurgatory`
    useEffect(() => {
        setLoading(true);
        setError(null);

        const q = query(
            collection(db, 'servers', serverID, 'onboardingPurgatory'),
            orderBy('updatedAt', 'desc'),
            limit(500)
        );

        const unsub = onSnapshot(
            q,
            (snapshot) => {
                const docs = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                setItems(docs);
                setLoading(false);
            },
            (err) => {
                console.error(`Failed to listen to onboardingPurgatory for ${serverID}:`, err);
                setError(err.message || 'Failed to load purgatory records from Firestore.');
                setLoading(false);
            }
        );

        return () => unsub();
    }, [serverID]);

    // 3. Auto-dismiss feedback toast after 6 seconds
    useEffect(() => {
        if (!toast) return;
        const timer = setTimeout(() => {
            setToast(null);
        }, 6000);
        return () => clearTimeout(timer);
    }, [toast]);

    // 4. Compute display status according to exact business rules
    const getDisplayStatus = (item) => {
        if (serverID !== '360camhs.co.uk' || (item.reviewStatus || 'pending') === 'rejected') {
            return (item.reviewStatus || 'pending').toLowerCase();
        }
        const rp = item.reviewProgress || {};
        if (rp.schemaVersion === 1 && rp.valid === true && rp.status === 'approved') {
            return 'approved';
        }
        const pr = item.profileReview || {};
        const lta = item.lastTrainingApproval || {};
        const hasApproval =
            item.reviewStatus === 'approved' ||
            pr.status === 'approved' ||
            (lta && Object.keys(lta).length > 0) ||
            (rp.decisions && Object.keys(rp.decisions).length > 0) ||
            rp.status === 'partially_approved' ||
            rp.status === 'approved';
        return hasApproval ? 'partially_approved' : 'pending';
    };

    // 5. Compute counts for each status chip
    const statusCounts = useMemo(() => {
        const counts = {
            pending: 0,
            partially_approved: 0,
            approved: 0,
            rejected: 0,
            all: items.length,
        };
        items.forEach((item) => {
            const ds = getDisplayStatus(item);
            if (counts[ds] !== undefined) {
                counts[ds]++;
            }
        });
        return counts;
    }, [items, serverID]);

    const countFor = (statusKey) => statusCounts[statusKey] || 0;

    // 6. Filter items by selectedStatus and search query
    const filteredItems = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        return items.filter((item) => {
            // Status filter
            if (selectedStatus !== 'all') {
                const ds = getDisplayStatus(item);
                if (ds !== selectedStatus) return false;
            }

            // Search query filter
            if (q) {
                const pd = item.payload?.personal_details || {};
                const candidateName = [pd.first_name, pd.middle_name || pd.middle_names, pd.last_name]
                    .filter(Boolean)
                    .join(' ') || item.candidateName || '';
                const candidateEmail = pd.email || item.payload?.email || item.candidateEmail || '';
                const employeeName = item.employeeName || '';
                const employeeEmail = item.employeeEmail || '';
                const candidateUserID = item.candidateUserID || item.payload?.user_id || item.payload?.userID || item.externalUserID || '';
                const employeeDocumentID = item.employeeDocumentID || '';
                const refNumber = item.payload?.reference_number || '';
                const rawAgencies = pd.agencies || pd.skills_and_agencies || item.payload?.agencies || item.payload?.skills_and_agencies || item.agencies;
                const agencies = Array.isArray(rawAgencies) ? rawAgencies.join(' ') : (typeof rawAgencies === 'string' ? rawAgencies : '');

                const searchable = `${candidateName} ${candidateEmail} ${employeeName} ${employeeEmail} ${candidateUserID} ${employeeDocumentID} ${refNumber} ${agencies}`.toLowerCase();
                if (!searchable.includes(q)) return false;
            }

            return true;
        });
    }, [items, selectedStatus, debouncedSearch, serverID]);

    // 7. Actions: Staging
    const handleTriggerStage = () => {
        setShowStageConfirmModal(true);
    };

    const handleConfirmStage = async () => {
        setShowStageConfirmModal(false);
        setStaging(true);
        try {
            const res = await stageOnboardingPurgatory(serverID);
            const msg = res?.message ||
                (res?.staged !== undefined
                    ? `Staging complete: ${res.staged} candidate records staged (${res.matched ?? 0} matched, ${res.unchanged ?? 0} unchanged).`
                    : `Synchronization scan initiated for ${BLEEP_SERVER_NAMES[serverID] || serverID}.`);
            setToast({ type: 'success', message: msg });
        } catch (err) {
            console.error('Stage purgatory error:', err);
            setToast({
                type: 'error',
                message: err?.message || 'Failed to stage latest changes from Onboarding360.',
            });
        } finally {
            setStaging(false);
        }
    };

    // 8. Actions: Approve
    const handleApprove = async (item) => {
        if (busyItemIds.has(item.id)) return;
        const candidateName = item.candidateName || item.candidateUserID || item.payload?.user_id || item.payload?.userID || 'this candidate';

        if (!window.confirm(`Are you sure you want to approve changes for ${candidateName} and apply them to ${BLEEP_SERVER_NAMES[serverID] || serverID}?`)) {
            return;
        }

        setBusyItemIds((prev) => new Set(prev).add(item.id));
        try {
            const res = await approvePurgatoryItem(serverID, item.id);
            const msg = res?.message || `Approved update for ${candidateName}.`;
            setToast({ type: 'success', message: msg });
        } catch (err) {
            console.error('Approve purgatory error:', err);
            setToast({
                type: 'error',
                message: err?.message || 'Failed to approve candidate update.',
            });
        } finally {
            setBusyItemIds((prev) => {
                const next = new Set(prev);
                next.delete(item.id);
                return next;
            });
        }
    };

    // 9. Actions: Reject
    const handleReject = async (item, reason) => {
        if (busyItemIds.has(item.id)) return;
        const candidateName = item.candidateName || item.candidateUserID || item.payload?.user_id || item.payload?.userID || 'this candidate';

        setBusyItemIds((prev) => new Set(prev).add(item.id));
        try {
            const res = await rejectPurgatoryItem(serverID, item.id, reason);
            const msg = res?.message || `Rejected update for ${candidateName}.`;
            setToast({ type: 'success', message: msg });
        } catch (err) {
            console.error('Reject purgatory error:', err);
            setToast({
                type: 'error',
                message: err?.message || 'Failed to reject candidate update.',
            });
        } finally {
            setBusyItemIds((prev) => {
                const next = new Set(prev);
                next.delete(item.id);
                return next;
            });
        }
    };

    return (
        <div>
            {/* Feedback Toast Notification */}
            {toast && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 18px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: toast.type === 'success' ? '#def7ec' : '#fde8e8',
                    color: toast.type === 'success' ? '#03543f' : '#9b1c1c',
                    border: `1px solid ${toast.type === 'success' ? '#bcf0da' : '#f8b4b4'}`,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {toast.type === 'success' ? (
                            <FaCheckCircle style={{ fontSize: '16px' }} />
                        ) : (
                            <FaExclamationCircle style={{ fontSize: '16px' }} />
                        )}
                        <span>{toast.message}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setToast(null)}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'inherit',
                            cursor: 'pointer',
                            padding: '4px',
                        }}
                    >
                        <FaTimes />
                    </button>
                </div>
            )}

            {/* Error Banner */}
            {error && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 18px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    backgroundColor: '#fff5f5',
                    color: '#c53030',
                    border: '1px solid #fed7d7',
                }}>
                    <FaExclamationTriangle style={{ fontSize: '18px', flexShrink: 0 }} />
                    <div style={{ flex: 1, fontSize: '13px' }}>
                        <strong>Firestore Connection Error:</strong> {error}
                    </div>
                </div>
            )}

            {/* Background Hourly Sync Status Bar */}
            <HourlySyncStatusBar serverID={serverID} />

            {/* Purgatory Toolbar */}
            <PurgatoryToolbar
                staging={staging}
                onStage={handleTriggerStage}
                onViewReports={() => setSearchParams({ server: serverID, tab: 'reports' })}
                selectedStatus={selectedStatus}
                onStatusChange={setSelectedStatus}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                countFor={countFor}
                statuses={PURGATORY_REVIEW_STATUSES}
            />

            {/* Main Content Area */}
            {loading ? (
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    padding: '60px 20px',
                    textAlign: 'center',
                    color: '#718096',
                }}>
                    <FaSpinner className="fa-spin" style={{ fontSize: '28px', color: '#2b6cb0', marginBottom: '12px' }} />
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>
                        Loading purgatory queue for {BLEEP_SERVER_NAMES[serverID] || serverID}…
                    </div>
                </div>
            ) : filteredItems.length === 0 ? (
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '10px',
                    border: '1px solid #e2e8f0',
                    padding: '60px 20px',
                    textAlign: 'center',
                    color: '#718096',
                }}>
                    <FaInbox style={{ fontSize: '42px', color: '#cbd5e0', marginBottom: '14px' }} />
                    <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '700', color: '#2d3748' }}>
                        No candidate records found
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#718096' }}>
                        {searchQuery
                            ? `No records match your search query "${searchQuery}" in ${selectedStatus} status.`
                            : `There are currently no records in the "${selectedStatus}" queue for ${BLEEP_SERVER_NAMES[serverID] || serverID}.`}
                    </p>
                    {(searchQuery || selectedStatus !== 'all') && (
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery('');
                                setSelectedStatus('all');
                            }}
                            style={{
                                marginTop: '16px',
                                backgroundColor: '#edf2f7',
                                color: '#2b6cb0',
                                border: '1px solid #cbd5e0',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: 'pointer',
                            }}
                        >
                            Reset filters & show all
                        </button>
                    )}
                </div>
            ) : (
                <div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '12px',
                        padding: '0 4px',
                        fontSize: '13px',
                        color: '#718096',
                        fontWeight: '600',
                    }}>
                        <span>Showing {filteredItems.length} of {items.length} records</span>
                        <span>Server: {BLEEP_SERVER_NAMES[serverID] || serverID}</span>
                    </div>

                    {filteredItems.map((item) => (
                        <PurgatoryCard
                            key={item.id}
                            item={item}
                            serverID={serverID}
                            displayStatus={getDisplayStatus(item)}
                            isBusy={busyItemIds.has(item.id)}
                            onApprove={handleApprove}
                            onReject={handleReject}
                        />
                    ))}
                </div>
            )}

            {/* Stage Confirmation Modal */}
            {showStageConfirmModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '480px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    }}>
                        <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: '700', color: '#2d3748' }}>
                            Stage Latest Changes?
                        </h3>
                        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: '#718096', lineHeight: '1.5' }}>
                            This will trigger a full synchronization scan against the Onboarding360 API for{' '}
                            <strong>{BLEEP_SERVER_NAMES[serverID] || serverID}</strong>. Incoming candidate updates will be staged into this review queue.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={() => setShowStageConfirmModal(false)}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmStage}
                                style={{
                                    backgroundColor: '#2b6cb0',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                }}
                            >
                                Stage Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PurgatoryReviewTab;
